var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { $auth } from 'ziphy-web-shared/basic/entities/auth';
import { anyRequiredVerified, hasRequiredAttachments, } from '@core/src/nedoShared/features/consultations/helpers/requiredAttachments';
import $livequeueChats from '@core/src/store/livequeueChatStore';
import livequeueConsultationsStore from '@core/src/store/livequeueConsultationsStore';
import $livequeueConsultation from '@store/livequeueConsultationsStore';
import { generateAndDownloadIcs } from '../../../utils/addToCalendar';
import { getLastAttachmentIdByRequirement, isLastAttachmentForRequirement, } from '../../../utils/attachmentsOrdering';
import { checkVerified } from '../../../utils/checkAttachmentVerified';
import { copyLink, showCopyNotification } from '../../../utils/copyLink';
import { openLink } from '../../../utils/openLink';
import { useResultModal } from '../../modals/TestModal/hooks/useResultModal';
export const useChatActions = (chatId, consultation, scrollToBottom) => {
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState('');
    const userId = $auth.user.id;
    const role = $auth.role.role;
    const roleId = $auth.role.id;
    const isProvider = role === 'provider';
    const { show: showResultModal, hide: hideResultModal, Modal: ResultModal } = useResultModal();
    const handleChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
    };
    const handleSend = () => {
        if (!inputValue.trim())
            return;
        // Fire-and-forget create; we optimistically append in the store
        $livequeueChats.createMessage(chatId, inputValue, roleId);
        // Immediately scroll to bottom to the outgoing message
        if (typeof scrollToBottom === 'function') {
            scrollToBottom();
        }
        setInputValue('');
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSend();
        }
    };
    const handleSubmit = (files) => __awaiter(void 0, void 0, void 0, function* () {
        if (files.length === 0)
            return;
        const imageUrls = files.map((file) => URL.createObjectURL(file));
        if (consultation) {
            yield $livequeueConsultation.getConsultationById(consultation._id);
        }
        // const newMessage: ChatMessage = {
        //   id: `msg-${Date.now()}`,
        //   sender: 'Outcome',
        //   message: '',
        //   time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        //   attachments: [
        //     {
        //       type: 'lab-result',
        //       text: 'Lab Result',
        //       images: imageUrls,
        //     },
        //   ],
        // }
        // onSendMessage?.(newMessage)
        hideResultModal();
    });
    const submitVerifyResults = (attachmentRequirementId, type, reason) => __awaiter(void 0, void 0, void 0, function* () {
        if (!consultation || !isProvider) {
            return;
        }
        yield livequeueConsultationsStore
            .verifyAttachmentRequirement(consultation._id, attachmentRequirementId, {
            verified: type === 'verify',
            reason: type === 'decline' ? reason : null,
        })
            .then(() => {
            hideResultModal();
        });
    });
    const handlePrintPDF = () => {
        console.log('Print PDF');
    };
    const handleCopyConferenceLink = (url) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const success = yield copyLink(url);
            if (success) {
                showCopyNotification(t('label.link_copied'));
            }
            else {
                alert('Cannot copy link. Please try again.');
            }
        }
        catch (error) {
            console.error('Error copying link:', error);
            alert('Error copying link.');
        }
    });
    const handleFileSelect = (files) => {
        if (!files)
            return;
        // TODO: upload attachments
    };
    const handleShowUploadModal = () => {
        var _a;
        showResultModal({
            role,
            type: 'upload',
            onSubmit: handleSubmit,
            onPrintPDF: handlePrintPDF,
            onFileSelected: handleFileSelect,
            onClose: () => console.log('Upload modal closed'),
            resultStatus: 'pending',
            consultation,
            attachment: (_a = consultation.attachments) === null || _a === void 0 ? void 0 : _a[0],
            showButtons: true,
        });
    };
    const handleShowResultsModal = (action) => {
        switch (action.type) {
            case 'add-to-calendar':
                generateAndDownloadIcs({
                    title: 'Telehealth Appointment',
                    description: 'Telehealth Appointment',
                    startTime: new Date(action.data.time),
                    endTime: new Date(action.data.time),
                    eventName: 'telehealth_appointment.ics',
                });
                break;
            case 'copy-conference-link':
                handleCopyConferenceLink(action.data.url);
                break;
            case 'join-conference':
                openLink(action.data.url);
                break;
            case 'print-pdf':
                // todo
                break;
            case 'upload-test-result':
                handleShowUploadModal();
                break;
            case 'check-results':
                // This action is context-bound in ChatComponent via a closure.
                // The handler for attachments is provided separately.
                break;
            case 'fill-intake':
                handleFillIntake();
                break;
        }
    };
    const onAttachmentClick = (attachment) => {
        var _a;
        const isVerified = checkVerified(consultation, attachment);
        const lastMap = getLastAttachmentIdByRequirement($livequeueChats.currentChatMessages);
        const isLast = isLastAttachmentForRequirement(attachment, lastMap);
        const resultStatus = isVerified === false
            ? 'rejected'
            : isLast && isVerified === true
                ? 'veryfied'
                : !isLast && isVerified === true
                    ? 'rejected'
                    : 'pending';
        let showButtons = false;
        if (attachment.attachment_requirement_id) {
            const requiredAttachment = (_a = consultation.attachments) === null || _a === void 0 ? void 0 : _a.find((a) => a.id === attachment.attachment_requirement_id);
            showButtons =
                !!requiredAttachment && (requiredAttachment === null || requiredAttachment === void 0 ? void 0 : requiredAttachment.verified) === null;
            if (attachment.upload_record_id && !isProvider) {
                showButtons = false;
            }
        }
        showResultModal({
            role,
            type: 'result',
            onSubmit: handleSubmit,
            onDecline: (reason) => submitVerifyResults(attachment.attachment_requirement_id, 'decline', reason),
            onPrintPDF: handlePrintPDF,
            images: [attachment.upload_record_id],
            onClose: () => console.log('Results modal closed'),
            resultStatus: resultStatus,
            onVerify: () => submitVerifyResults(attachment.attachment_requirement_id, 'verify'),
            consultation,
            attachment: attachment,
            showButtons: showButtons,
        });
    };
    const handleCheckResultsForAttachments = (consultation, attachments) => {
        var _a;
        const resultStatus = hasRequiredAttachments(consultation)
            ? anyRequiredVerified(consultation)
                ? 'veryfied'
                : 'pending'
            : 'pending';
        const attachment = attachments[0];
        const requiredAttachment = (_a = consultation.attachments) === null || _a === void 0 ? void 0 : _a.find((a) => a.id === attachment.attachment_requirement_id);
        const showButtons = !!requiredAttachment && (requiredAttachment === null || requiredAttachment === void 0 ? void 0 : requiredAttachment.verified) === null;
        showResultModal({
            role,
            type: 'result',
            images: attachments.map((a) => a.upload_record_id),
            onClose: () => console.log('Results modal (list) closed'),
            resultStatus: resultStatus,
            consultation,
            onVerify: () => submitVerifyResults(attachments[0].attachment_requirement_id, 'verify'),
            onDecline: (reason) => submitVerifyResults(attachments[0].attachment_requirement_id, 'decline', reason),
            attachment: attachments[0],
            showButtons: showButtons,
        });
    };
    const handleAddProviderAttachment = () => {
        console.log('add provider attachment');
    };
    const handleCreateChat = () => {
        $livequeueChats.createChat();
    };
    const handleSubscribeToChat = () => {
        $livequeueChats.subscribeToChat(chatId, userId);
    };
    const handleFillIntake = () => {
        // TODO: fill intake
    };
    return {
        handleChange,
        inputValue,
        handleKeyDown,
        ResultModal,
        handleSend,
        handleShowResultsModal,
        onAttachmentClick,
        handleShowUploadModal,
        handleFillIntake,
        handleCheckResultsForAttachments,
        handleAddProviderAttachment,
    };
};
